// Legacy formatting helpers aligned to AngularJS filters.
// Source: public/SPIGlassApp/directives/filters.js (tel filter lines ~216-246)
export function formatPhone(value: string): string {
  if (!value) return '';
  const digits = value.replace(/[^0-9]/g, '').slice(0, 10);
  if (!digits) return '';

  let city = '';
  let number = '';

  if (digits.length <= 3) {
    city = digits;
  } else {
    city = digits.slice(0, 3);
    number = digits.slice(3);
  }

  if (number) {
    if (number.length > 3) {
      number = number.slice(0, 3) + '-' + number.slice(3, 7);
    }
    return `(${city}) ${number}`.trim();
  }

  return `(${city}`;
}

export function unformatPhone(value: string): string {
  if (!value) return '';
  return value.replace(/[^0-9]/g, '').slice(0, 10);
}

/**
 * As-you-type phone input formatter.
 * Unlike formatPhone (which adds parenthesis for 1-3 digits), this returns
 * raw digits until 4+ characters are typed, making it suitable for onChange handlers.
 */
export function formatPhoneInput(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) {
    return digits;
  }
  if (digits.length <= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  }
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

/**
 * Strips all non-digit characters from a phone string.
 * Unlike unformatPhone, does NOT limit to 10 digits.
 */
export function stripPhone(value: string | undefined): string {
  if (!value) return '';
  return value.replace(/\D/g, '');
}

// ---------------------------------------------------------------------------
// Date formatting helpers
// Source: consolidated from inline implementations across admin + vendor forms
// ---------------------------------------------------------------------------

/** Loose timestamp shape returned by gRPC / .NET JSON serialisation. */
export type TimestampLike =
  | Date
  | string
  | number
  | { seconds?: number; Seconds?: number; nanos?: number; Nanos?: number }
  | null
  | undefined;

/** Coerce any timestamp-like value into a native Date (or null). */
export function coerceToDate(value: TimestampLike): Date | null {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'string') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === 'number') {
    const ms = value > 1e12 ? value : value * 1000;
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const seconds = value.seconds ?? value.Seconds;
  if (typeof seconds === 'number') {
    const nanos = value.nanos ?? value.Nanos ?? 0;
    const date = new Date(seconds * 1000 + Math.floor(nanos / 1_000_000));
    return Number.isNaN(date.getTime()) ? null : date;
  }
  return null;
}

/** Format any timestamp-like value as MM/DD/YYYY (local time). */
export function formatDate(value: TimestampLike): string {
  const date = coerceToDate(value);
  if (!date) return '';
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${mm}/${dd}/${date.getFullYear()}`;
}

/** Format any timestamp-like value as MM/DD/YYYY - HH:mm:ss (local time). */
export function formatDateTime(value: TimestampLike): string {
  const date = coerceToDate(value);
  if (!date) return '';
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  const ss = String(date.getSeconds()).padStart(2, '0');
  return `${mm}/${dd}/${date.getFullYear()} - ${hh}:${min}:${ss}`;
}

/** Format any timestamp-like value as M/d/yy, h:mm AM/PM (matches AngularJS date:'short'). */
export function formatDateShort(value: TimestampLike): string {
  const date = coerceToDate(value);
  if (!date) return '';
  const m = date.getMonth() + 1;
  const d = date.getDate();
  const yy = String(date.getFullYear()).slice(-2);
  let hours = date.getHours();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${m}/${d}/${yy} ${hours}:${min} ${ampm}`;
}

/** Format any timestamp-like value as M/d/yy (date-only, no time — matches AngularJS date:'shortDate'). */
export function formatShortDateOnly(value: TimestampLike): string {
  const date = coerceToDate(value);
  if (!date) return '';
  const m = date.getMonth() + 1;
  const d = date.getDate();
  const yy = String(date.getFullYear()).slice(-2);
  return `${m}/${d}/${yy}`;
}

/** Format any timestamp-like value as MM-DD-YYYY (dashes — matches AngularJS date:'MM-dd-yyyy'). */
export function formatDateDashed(value: TimestampLike): string {
  const date = coerceToDate(value);
  if (!date) return '';
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${mm}-${dd}-${date.getFullYear()}`;
}

/** Format a Date as MM/DD/YYYY using UTC methods. */
export function formatDateUTC(value: Date | null | undefined): string {
  if (!value) return '';
  const mm = String(value.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(value.getUTCDate()).padStart(2, '0');
  return `${mm}/${dd}/${value.getUTCFullYear()}`;
}

/** Validate an MM/DD/YYYY date string (mirrors legacy common.isValidDate). */
export function isValidDisplayDate(dateString: string): boolean {
  if (!/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString)) return false;
  const parts = dateString.split('/');
  const month = parseInt(parts[0], 10);
  const day = parseInt(parts[1], 10);
  const year = parseInt(parts[2], 10);
  if (year < 1000 || year > 3000 || month === 0 || month > 12) return false;
  const monthLength = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (year % 400 === 0 || (year % 100 !== 0 && year % 4 === 0)) monthLength[1] = 29;
  return day > 0 && day <= monthLength[month - 1];
}

/** Convert MM/DD/YYYY display date → YYYY-MM-DD native input value. */
export function toNativeDateValue(displayDate: string): string {
  if (!displayDate || !isValidDisplayDate(displayDate)) return '';
  const [m, d, y] = displayDate.split('/').map(Number);
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** Convert YYYY-MM-DD native input value → MM/DD/YYYY display date. */
export function fromNativeDateValue(nativeValue: string): string {
  if (!nativeValue) return '';
  const [y, m, d] = nativeValue.split('-').map(Number);
  if (!y || !m || !d) return '';
  return `${String(m).padStart(2, '0')}/${String(d).padStart(2, '0')}/${y}`;
}

/** Parse MM/DD/YYYY display date into a UTC Date (for API payloads). */
export function parseDisplayDate(value: string | null | undefined): Date | null {
  if (!value || !isValidDisplayDate(value)) return null;
  const [m, d, y] = value.split('/').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

/** Convert MM/DD/YYYY → { Seconds, Nanos } epoch payload for gRPC APIs. */
export function toEpochPayload(
  value: string | null | undefined
): { Seconds: number; Nanos: 0 } | null {
  if (!value || !isValidDisplayDate(value)) return null;
  const [m, d, y] = value.split('/').map(Number);
  const date = new Date(y, m - 1, d);
  return { Seconds: date.getTime() / 1000, Nanos: 0 };
}

/**
 * Format a numeric string as currency input: $1,234.56
 * Matches AngularJS `format-input-field-currency` directive behavior.
 * - Strips non-numeric/dot characters
 * - Adds $ prefix and comma grouping
 * - Limits decimal to 2 digits
 */
export function formatCurrencyInput(raw: string): string {
  const stripped = raw.replace(/[^\d.]/g, '');
  if (!stripped) return '';

  const dotIndex = stripped.indexOf('.');
  if (dotIndex >= 0) {
    const left = stripped.substring(0, dotIndex).replace(/\D/g, '');
    const right = stripped
      .substring(dotIndex + 1)
      .replace(/\D/g, '')
      .substring(0, 2);
    const formattedLeft = left.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return `$${formattedLeft}.${right}`;
  }

  const digits = stripped.replace(/\D/g, '');
  const formatted = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `$${formatted}`;
}

/**
 * Strip currency formatting ($, commas) to get raw numeric string.
 * Inverse of formatCurrencyInput.
 */
export function stripCurrency(value: string): string {
  return value.replace(/[^\d.]/g, '');
}

/**
 * Format a number as currency display: $1,234.56
 * Matches AngularJS `$filter('currency')` behavior.
 */
export function formatCurrencyDisplay(value: number | string): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '';
  return `$${num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
}

/**
 * Format a number as currency, safely handling very large numbers that JS
 * stringifies with scientific notation (>= ~1e21). For those, the comma-
 * insertion regex in `formatCurrencyDisplay` mangles the exponent
 * (e.g. "2.33e+167" → "2,3,3...e+167"). This variant uses `toExponential(2)`
 * for scientific-notation values — 2-decimal mantissa precision matches
 * AngularJS's `currency` filter (e.g. "2.3333...e+52" → "2.33e+52").
 * Small numbers format identically to `formatCurrencyDisplay`.
 */
export function formatCurrencyDisplaySafe(value: number | string): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return '';
  // Match AngularJS `currency` filter (Intl.NumberFormat) which renders
  // ±Infinity as "$∞" / "-$∞" instead of the raw "Infinity" toString.
  if (!Number.isFinite(num)) {
    return num > 0 ? '$∞' : '-$∞';
  }
  if (num.toString().indexOf('e') !== -1) {
    return `$${num.toExponential(2)}`;
  }
  return `$${num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
}
